import React, { useState, useEffect } from "react";
import axios from "axios";
import { MyDomain } from "../../App";
import { useAppContext } from "../../AppContext";
import { useTranslation } from "react-i18next";

// ─── Tokens ───────────────────────────────────────────────────────────────────
const C = {
  primary:     "#1A1A2E",
  accent:      "#C9A84C",
  accentLight: "#FDF6E3",
  bg:          "#F4F5F7",
  border:      "#E8ECF0",
  textMuted:   "#A0AEC0",
};

export default function MPHomeShowImages() {
  const [windowWidth,       setWindowWidth]       = useState(window.innerWidth);
  const [mainImage,         setMainImage]         = useState(null);
  const [mainImage2,        setMainImage2]        = useState(null);
  const [mainImage3,        setMainImage3]        = useState(null);
  const [galleryImages,     setGalleryImages]     = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [selectedImage,     setSelectedImage]     = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoadError,    setImageLoadError]    = useState({});
  const { t }       = useTranslation();
  const { AllData } = useAppContext();

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (AllData?.barberData?.id) fetchImages();
  }, [AllData?.barberData?.id]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${MyDomain}Barbers2/GetAllImagesByBarberId/${AllData.barberData.id}`
      );
      setMainImage(data.mainImage || null);
      setMainImage2(data.mainImage2 || null);
      setMainImage3(data.mainImage3 || null);
      setGalleryImages(data.galleryImages || []);
    } catch {
      setMainImage(null); setMainImage2(null);
      setMainImage3(null); setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  };

  const allImagesForLightbox = [
    ...(mainImage  ? [{ id: mainImage.id,  image: mainImage.image }]  : []),
    ...(mainImage2 ? [{ id: mainImage2.id, image: mainImage2.image }] : []),
    ...(mainImage3 ? [{ id: mainImage3.id, image: mainImage3.image }] : []),
    ...galleryImages.map(img => ({ ...img })),
  ];

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = "auto";
  };

  const navigate = (dir) => {
    const next = dir === "next"
      ? (currentImageIndex + 1) % allImagesForLightbox.length
      : (currentImageIndex - 1 + allImagesForLightbox.length) % allImagesForLightbox.length;
    setCurrentImageIndex(next);
    setSelectedImage(allImagesForLightbox[next].image);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!selectedImage) return;
      if (e.key === "Escape")     closeLightbox();
      if (e.key === "ArrowLeft")  navigate("prev");
      if (e.key === "ArrowRight") navigate("next");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedImage, currentImageIndex]);

  const isMobile  = windowWidth <= 768;
  const hasMain   = mainImage || mainImage2 || mainImage3;
  const totalImgs = allImagesForLightbox.length;

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: "40px 20px", display: "flex", flexDirection: "column",
      alignItems: "center", gap: 14 }}>
      <style>{`@keyframes msi-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{
        width: 72, height: 72, borderRadius: "50%",
        background: C.accentLight, border: `2px solid ${C.accent}44`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <span style={{ fontSize: 28 }}>🖼️</span>
      </div>
      <div style={{
        width: 36, height: 36, border: `3px solid ${C.border}`,
        borderTop: `3px solid ${C.accent}`, borderRadius: "50%",
        animation: "msi-spin 0.9s linear infinite",
      }} />
      <span style={{ fontSize: 13, color: C.textMuted, fontWeight: 500 }}>
        {t("Loading gallery...") || "Loading gallery…"}
      </span>
    </div>
  );

  // ─── Empty ──────────────────────────────────────────────────────────────────
  if (!hasMain && !galleryImages.length) return (
    <div style={{ padding: "48px 24px", display: "flex", flexDirection: "column",
      alignItems: "center", gap: 10, textAlign: "center" }}>
      <span style={{ fontSize: 44, opacity: 0.2 }}>📷</span>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.primary }}>
        {t("No Images Yet") || "No Images Yet"}
      </div>
      <div style={{ fontSize: 13, color: C.textMuted }}>
        {t("Check back soon for our latest work") || "Check back soon for our latest work"}
      </div>
    </div>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes msi-spin { to { transform: rotate(360deg); } }
        @keyframes msi-fadeIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }

        .msi-img-wrap:hover .msi-overlay  { opacity: 1 !important; }
        .msi-img-wrap:hover img            { transform: scale(1.06) !important; }
        .msi-img-wrap:hover                { box-shadow: 0 10px 28px rgba(201,168,76,0.28) !important; }

        .msi-gallery-item:hover .msi-overlay { opacity: 1 !important; }
        .msi-gallery-item:hover img          { transform: scale(1.07) !important; }
        .msi-gallery-item:hover              { box-shadow: 0 8px 22px rgba(0,0,0,0.16) !important; transform: translateY(-2px); }

        .msi-lb-btn:hover { background: rgba(201,168,76,0.25) !important; border-color: ${C.accent} !important; }
        .msi-lb-close:hover { transform: rotate(90deg) !important; }
        .msi-lb-prev:hover { transform: translateY(-50%) scale(1.12) !important; }
        .msi-lb-next:hover { transform: translateY(-50%) scale(1.12) !important; }

        .msi-gallery-scroll::-webkit-scrollbar { height: 4px; }
        .msi-gallery-scroll::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 2px; }
      `}</style>

      <div style={{ width: "100%", fontFamily: "'Segoe UI', sans-serif" }}>

        {/* ── Section Header ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: isMobile ? "16px 16px 10px" : "20px 20px 12px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: C.primary,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke={C.accent} strokeWidth="2" strokeLinecap="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: C.primary }}>
              {t("Gallery") || "Gallery"}
            </span>
          </div>
          {totalImgs > 0 && (
            <span style={{
              fontSize: 12, fontWeight: 600, color: C.accent,
              background: C.accentLight, padding: "3px 10px",
              borderRadius: 20, border: `1px solid ${C.accent}44`,
            }}>
              {totalImgs} {t("photos") || "photos"}
            </span>
          )}
        </div>

        {/* ── Featured 3 images grid ── */}
        {hasMain && (
          <div style={{
            padding: isMobile ? "0 10px 10px" : "0 16px 14px",
            display: "grid",
            gridTemplateColumns: mainImage && (mainImage2 || mainImage3)
              ? (isMobile ? "1fr 1fr" : "2fr 1fr")
              : "1fr",
            gridTemplateRows: isMobile ? "auto" : "auto",
            gap: 6,
          }}>

            {/* Main image — spans 2 rows on desktop when siblings exist */}
            {mainImage && (
              <FeaturedImg
                src={mainImage.image}
                hasError={imageLoadError[mainImage.id]}
                onError={() => setImageLoadError(p => ({ ...p, [mainImage.id]: true }))}
                onClick={() => openLightbox(mainImage.image, 0)}
                height={isMobile ? 200 : 300}
                gridRow={(mainImage2 || mainImage3) && !isMobile ? "1 / 3" : undefined}
                badge={
                  <div style={{
                    position: "absolute", top: 8, left: 8, zIndex: 3,
                    background: C.accent, color: C.primary,
                    padding: "3px 8px", borderRadius: 20,
                    fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
                    display: "flex", alignItems: "center", gap: 3,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  }}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill={C.primary}>
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    {t("Main") || "Main"}
                  </div>
                }
              />
            )}

            {/* Secondary images — right column */}
            {mainImage2 && (
              <FeaturedImg
                src={mainImage2.image}
                hasError={imageLoadError[mainImage2.id]}
                onError={() => setImageLoadError(p => ({ ...p, [mainImage2.id]: true }))}
                onClick={() => openLightbox(mainImage2.image, mainImage ? 1 : 0)}
                height={isMobile ? 120 : (mainImage3 ? 147 : 300)}
                badge={
                  <div style={{
                    position: "absolute", bottom: 6, right: 6, zIndex: 3,
                    background: "rgba(0,0,0,0.55)", color: "#fff",
                    width: 20, height: 20, borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700,
                  }}>2</div>
                }
              />
            )}
            {mainImage3 && (
              <FeaturedImg
                src={mainImage3.image}
                hasError={imageLoadError[mainImage3.id]}
                onError={() => setImageLoadError(p => ({ ...p, [mainImage3.id]: true }))}
                onClick={() => {
                  let idx = 0;
                  if (mainImage)  idx++;
                  if (mainImage2) idx++;
                  openLightbox(mainImage3.image, idx);
                }}
                height={isMobile ? 120 : (mainImage2 ? 147 : 300)}
                badge={
                  <div style={{
                    position: "absolute", bottom: 6, right: 6, zIndex: 3,
                    background: "rgba(0,0,0,0.55)", color: "#fff",
                    width: 20, height: 20, borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700,
                  }}>3</div>
                }
              />
            )}
          </div>
        )}

        {/* ── Gallery grid ── */}
        {galleryImages.length > 0 && (
          <div style={{ padding: isMobile ? "0 10px 16px" : "0 16px 20px" }}>
            {/* Sub-header for gallery */}
            {hasMain && (
              <div style={{
                fontSize: 12, fontWeight: 600, color: C.textMuted,
                textTransform: "uppercase", letterSpacing: 0.8,
                marginBottom: 8,
              }}>
                {t("More Work") || "More Work"} · {galleryImages.length}
              </div>
            )}

            {/* Responsive grid on desktop, 2-col on mobile */}
            {isMobile ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 6,
              }}>
                {galleryImages.map((item, index) => {
                  let lbIdx = index;
                  if (mainImage)  lbIdx++;
                  if (mainImage2) lbIdx++;
                  if (mainImage3) lbIdx++;
                  return (
                    <GalleryItem key={item.id}
                      item={item} index={index} lbIdx={lbIdx}
                      hasError={imageLoadError[item.id]}
                      onError={() => setImageLoadError(p => ({ ...p, [item.id]: true }))}
                      onClick={() => openLightbox(item.image, lbIdx)}
                      size={150}
                    />
                  );
                })}
              </div>
            ) : (
              /* Horizontal scroll on desktop — larger thumbnails */
              <div className="msi-gallery-scroll" style={{
                display: "flex", gap: 8, overflowX: "auto",
                paddingBottom: 6, scrollBehavior: "smooth",
              }}>
                {galleryImages.map((item, index) => {
                  let lbIdx = index;
                  if (mainImage)  lbIdx++;
                  if (mainImage2) lbIdx++;
                  if (mainImage3) lbIdx++;
                  return (
                    <GalleryItem key={item.id}
                      item={item} index={index} lbIdx={lbIdx}
                      hasError={imageLoadError[item.id]}
                      onError={() => setImageLoadError(p => ({ ...p, [item.id]: true }))}
                      onClick={() => openLightbox(item.image, lbIdx)}
                      size={200}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Lightbox ── */}
      {selectedImage && (
        <div onClick={closeLightbox} style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.96)", backdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999, animation: "msi-fadeIn 0.2s ease",
        }}>
          {/* Close */}
          <button className="msi-lb-btn msi-lb-close" onClick={closeLightbox}
            style={{
              position: "fixed", top: 16, right: 16,
              width: 40, height: 40, borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
              border: `1.5px solid rgba(255,255,255,0.2)`,
              color: "#fff", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s", zIndex: 10001,
            }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          {/* Image */}
          <div onClick={e => e.stopPropagation()} style={{
            position: "relative", maxWidth: "90vw", maxHeight: "88vh",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <img src={selectedImage}
              alt={`${t("Gallery image") || "Gallery image"} ${currentImageIndex + 1}`}
              style={{
                maxWidth: "100%", maxHeight: "88vh",
                objectFit: "contain", borderRadius: 10,
                boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
              }}
            />
          </div>

          {/* Counter */}
          <div style={{
            position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
            border: `1px solid ${C.accent}44`,
            padding: "6px 16px", borderRadius: 30,
            color: C.accent, fontSize: 13, fontWeight: 700, zIndex: 10001,
          }}>
            {currentImageIndex + 1} / {totalImgs}
          </div>

          {/* Prev / Next */}
          {totalImgs > 1 && (
            <>
              <button className="msi-lb-btn msi-lb-prev"
                onClick={e => { e.stopPropagation(); navigate("prev"); }}
                style={{
                  position: "fixed", left: 16, top: "50%", transform: "translateY(-50%)",
                  width: 44, height: 44, borderRadius: "50%",
                  background: "rgba(255,255,255,0.08)",
                  border: `1.5px solid rgba(255,255,255,0.2)`,
                  color: "#fff", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s", zIndex: 10001,
                }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button className="msi-lb-btn msi-lb-next"
                onClick={e => { e.stopPropagation(); navigate("next"); }}
                style={{
                  position: "fixed", right: 16, top: "50%", transform: "translateY(-50%)",
                  width: 44, height: 44, borderRadius: "50%",
                  background: "rgba(255,255,255,0.08)",
                  border: `1.5px solid rgba(255,255,255,0.2)`,
                  color: "#fff", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s", zIndex: 10001,
                }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

// ─── Featured image tile ───────────────────────────────────────────────────────
function FeaturedImg({ src, hasError, onError, onClick, height, gridRow, badge }) {
  return (
    <div className="msi-img-wrap" onClick={onClick}
      style={{
        position: "relative", borderRadius: 14, overflow: "hidden",
        height, gridRow, cursor: "pointer",
        background: "#EAECEF",
        boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
        transition: "box-shadow 0.25s, transform 0.25s",
      }}>
      {badge}
      {hasError ? (
        <ErrPlaceholder />
      ) : (
        <img src={src} alt="" onError={onError}
          style={{ width: "100%", height: "100%", objectFit: "cover",
            transition: "transform 0.35s ease", display: "block" }} />
      )}
      {/* Hover overlay */}
      <div className="msi-overlay" style={{
        position: "absolute", inset: 0, zIndex: 2,
        background: "linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.55) 100%)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        paddingBottom: 14, opacity: 0, transition: "opacity 0.25s",
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 5,
          background: "rgba(201,168,76,0.18)", border: "1px solid rgba(201,168,76,0.5)",
          borderRadius: 20, padding: "4px 12px",
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#C9A84C", letterSpacing: 0.5 }}>
            VIEW
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Gallery thumbnail ────────────────────────────────────────────────────────
function GalleryItem({ item, index, hasError, onError, onClick, size }) {
  return (
    <div className="msi-gallery-item" onClick={onClick}
      style={{
        position: "relative", flexShrink: 0,
        width: size, height: size,
        borderRadius: 12, overflow: "hidden",
        background: "#EAECEF", cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0,0,0,0.09)",
        transition: "box-shadow 0.2s, transform 0.2s",
      }}>
      {hasError ? (
        <ErrPlaceholder />
      ) : (
        <img src={item.image}
          alt={`Gallery ${index + 1}`}
          onError={onError}
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover",
            transition: "transform 0.3s ease" }} />
      )}
      <div className="msi-overlay" style={{
        position: "absolute", inset: 0,
        background: "rgba(26,26,46,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        opacity: 0, transition: "opacity 0.2s",
      }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
          <line x1="11" y1="8" x2="11" y2="14"/>
          <line x1="8" y1="11" x2="14" y2="11"/>
        </svg>
      </div>
    </div>
  );
}

// ─── Error placeholder ────────────────────────────────────────────────────────
function ErrPlaceholder() {
  return (
    <div style={{ width: "100%", height: "100%", background: "#F0F2F5",
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="#A0AEC0" strokeWidth="1.5">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="9" y1="9" x2="15" y2="15"/>
        <line x1="15" y1="9" x2="9" y2="15"/>
      </svg>
    </div>
  );
}
